/**
 * Service Discovery
 *
 * Fetches and caches service configuration from the .well-known/service-configuration endpoint.
 * Adapted from the browser extension implementation.
 */
import { SERVICE_RELS, STORAGE_KEYS, } from './types';
/** Cache TTL: 60 minutes */
const CACHE_TTL_MS = 60 * 60 * 1000;
/** Current cache version - increment when cache format changes */
const CACHE_VERSION = 1;
/** Discovery request timeout: 5 seconds */
const DISCOVERY_TIMEOUT_MS = 5000;
/**
 * Fetch the service configuration from the well-known endpoint
 */
async function fetchServiceConfiguration(baseUrl) {
    const configUrl = `${baseUrl.replace(/\/$/, '')}/.well-known/service-configuration`;
    console.log('[ServiceDiscovery] Fetching configuration from:', configUrl);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS);
    try {
        const response = await fetch(configUrl, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            credentials: 'omit',
            signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
            console.warn('[ServiceDiscovery] Failed to fetch configuration:', response.status, response.statusText);
            return null;
        }
        const config = await response.json();
        console.log('[ServiceDiscovery] Received configuration:', config);
        return config;
    }
    catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            console.warn('[ServiceDiscovery] Request timed out after', DISCOVERY_TIMEOUT_MS, 'ms');
        }
        else {
            console.warn('[ServiceDiscovery] Error fetching configuration:', error);
        }
        return null;
    }
}
/**
 * Parse and validate the service configuration response
 */
function parseServiceConfiguration(fallbackAppUrl, config) {
    try {
        // Handle both array format and { links: [...] } wrapper format
        // The backend returns { links: [...] } but we also support direct array
        const links = Array.isArray(config) ? config : config?.links;
        if (!Array.isArray(links)) {
            console.warn('[ServiceDiscovery] Invalid configuration format - expected array or { links: [...] }');
            return null;
        }
        // Find required links
        const identityLink = links.find((link) => link.rel === SERVICE_RELS.IDENTITY_PROVIDER);
        const apiLink = links.find((link) => link.rel === SERVICE_RELS.API);
        const officeLink = links.find((link) => link.rel === SERVICE_RELS.OFFICE);
        const chatLink = links.find((link) => link.rel === SERVICE_RELS.CHAT);
        // Validate required links
        if (!identityLink || !apiLink) {
            console.warn('[ServiceDiscovery] Missing required links. Identity:', !!identityLink, 'API:', !!apiLink);
            return null;
        }
        // Validate URLs
        const apiUrl = new URL(apiLink.href);
        const identityUrl = new URL(identityLink.href);
        // Office URL is optional - fall back to current origin or fallback URL
        let officeUrl;
        if (officeLink) {
            officeUrl = new URL(officeLink.href).origin;
        }
        else {
            console.log('[ServiceDiscovery] No office rel found, using fallback URL');
            officeUrl = fallbackAppUrl;
        }
        // Hub URL: ACC is co-hosted with the chat application (not the API).
        // Fall back to API origin if the chat rel is absent.
        let hubUrl;
        if (chatLink) {
            hubUrl = new URL(chatLink.href).origin + '/browseragenthub';
        }
        else {
            console.log('[ServiceDiscovery] No chat rel found, deriving hub URL from API');
            hubUrl = apiUrl.origin + '/browseragenthub';
        }
        const resolved = {
            appUrl: fallbackAppUrl,
            apiUrl: apiUrl.origin,
            authHost: identityUrl.host,
            officeUrl,
            hubUrl,
        };
        console.log('[ServiceDiscovery] Parsed configuration:', resolved);
        return resolved;
    }
    catch (error) {
        console.warn('[ServiceDiscovery] Error parsing configuration:', error);
        return null;
    }
}
/**
 * Get cached service configuration
 */
function getCachedConfig(appUrl) {
    try {
        const cached = localStorage.getItem(STORAGE_KEYS.SERVICE_CONFIG_CACHE);
        if (!cached) {
            return null;
        }
        const parsed = JSON.parse(cached);
        // Check cache version
        if (parsed.version !== CACHE_VERSION) {
            console.log('[ServiceDiscovery] Cache version mismatch, invalidating');
            return null;
        }
        // Check if cache is for the same app URL
        if (parsed.appUrl !== appUrl) {
            console.log('[ServiceDiscovery] Cache app URL mismatch, invalidating');
            return null;
        }
        // Check if cache has expired
        const age = Date.now() - parsed.cachedAt;
        if (age > CACHE_TTL_MS) {
            console.log('[ServiceDiscovery] Cache expired (age:', Math.round(age / 1000), 's)');
            return null;
        }
        console.log('[ServiceDiscovery] Using cached configuration (age:', Math.round(age / 1000), 's)');
        return parsed.config;
    }
    catch (error) {
        console.warn('[ServiceDiscovery] Error reading cache:', error);
        return null;
    }
}
/**
 * Save service configuration to cache
 */
function setCachedConfig(appUrl, config) {
    try {
        const cached = {
            config,
            cachedAt: Date.now(),
            appUrl,
            version: CACHE_VERSION,
        };
        localStorage.setItem(STORAGE_KEYS.SERVICE_CONFIG_CACHE, JSON.stringify(cached));
        console.log('[ServiceDiscovery] Configuration cached');
    }
    catch (error) {
        console.warn('[ServiceDiscovery] Error saving cache:', error);
    }
}
/**
 * Clear the service configuration cache
 */
export function clearServiceConfigCache() {
    try {
        localStorage.removeItem(STORAGE_KEYS.SERVICE_CONFIG_CACHE);
        console.log('[ServiceDiscovery] Cache cleared');
    }
    catch (error) {
        console.warn('[ServiceDiscovery] Error clearing cache:', error);
    }
}
/**
 * Get service configuration for a given app URL
 * Uses caching with 60-minute TTL
 *
 * @param discoveryBaseUrl - The base URL for the frontend app (e.g., "https://eu1.airia.ai")
 * @param fallbackOfficeUrl - Fallback office URL if not in discovery response
 * @returns Resolved service configuration or null if discovery fails
 */
export async function getServiceConfiguration(discoveryBaseUrl, fallbackOfficeUrl) {
    // Normalize URL
    const normalizedUrl = discoveryBaseUrl.replace(/\/$/, '');
    // Check cache first
    const cached = getCachedConfig(normalizedUrl);
    if (cached) {
        return cached;
    }
    // Fetch fresh configuration
    const response = await fetchServiceConfiguration(normalizedUrl);
    if (!response) {
        return null;
    }
    // Parse configuration
    const parsed = parseServiceConfiguration(fallbackOfficeUrl || normalizedUrl, response);
    if (!parsed) {
        return null;
    }
    // Cache the result
    setCachedConfig(normalizedUrl, parsed);
    return parsed;
}
